import { IconCopy, IconCopyCheck } from '@tabler/icons-react';
import { useState } from 'react';

type GradientButtonProps = {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
};

export const GradientButton: React.FC<GradientButtonProps> = ({
  children,
  onClick,
  className = '',
  disabled = false,
  type = 'button',
}) => {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`p-3 font-medium text-white text-sm rounded-xl bg-gradient-to-b from-[#373939] to-[#1a1a1a] hover:bg-gradient-to-t hover:from-[#373939] hover:to-[#1a1a1a] transition-all duration-300 ease-in-out ${className}`}
    >
      {children}
    </button>
  );
};

export function IconButton({
  children,
  className = '',
  disabled,
  ...props
}: {
  children: React.ReactNode;
  className?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const baseClasses =
    'p-2 px-3 rounded-md outline-none flex items-center justify-center space-x-2 drop-shadow-sm';
  const enabledClasses = 'bg-white text-black border cursor-pointer';
  const disabledClasses = 'bg-gray-200 text-gray-500 border-gray-300 cursor-not-allowed';

  return (
    <button
      className={`${baseClasses} ${disabled ? disabledClasses : enabledClasses} ${className}`}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
}

type CopyButtonProps = {
  data: string;
  title: string;
  filled?: boolean;
  className?: string;
  disabled?: boolean;
};

export const CopyButton: React.FC<CopyButtonProps> = ({
  data,
  className = '',
  filled = false,
  title = 'Copy to clipboard',
}) => {
  const [copyStatus, setCopyStatus] = useState(false);

  const handleDataCopy = async (data: string) => {
    try {
      await navigator.clipboard.writeText(data);
      setCopyStatus(true);
      setTimeout(() => setCopyStatus(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <span
      className={`ml-5 border ${filled ? 'bg-gray-400 text-white' : 'border-gray-400'}  text-black p-1 pl-3 pr-3 rounded-md text-sm flex flex-row items-center ${className}`}
    >
      <span>{data}</span>
      {copyStatus ? (
        <IconCopyCheck
          size="1.2rem"
          className={`ml-3 ${filled ? 'text-white hover:text-slate-200' : 'text-slate-600 hover:text-slate-80'} cursor-pointer`}
          title={title}
          onClick={() => handleDataCopy(data)}
        />
      ) : (
        <IconCopy
          size="1.2rem"
          className={`ml-3 ${filled ? 'text-white hover:text-slate-200' : 'text-slate-600 hover:text-slate-800'} cursor-pointer`}
          title={title}
          onClick={() => handleDataCopy(data)}
        />
      )}
    </span>
  );
};
